import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { useSharedLoan } from 'context/SharedLoanContext'
import {
  asPrepaymentPenaltyList,
  PrepaymentPenalty,
} from 'pages/Admin/AdminTools/Configuration/Servicing/PrepaymentPenalty/constant'
import { useEffect, useMemo, useState } from 'react'
import { getAdminConfig, getPenalty, postPenalty } from 'services'
import { Button } from 'stories/components'
import { ConvertInputValue, formatDate } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { renderParagraph } from 'utils/RenderParagraph'
import { setLoanNumber } from 'utils/setLoanNumber'

import { paragraphs } from './constants'
import { declarationsPenaltyValidate } from './logic'

export default function Penalties() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [inputs, setInputs] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<any>({})
  const [valueChanged, setValueChanged] = useState(false)
  const [prepaymentPenaltyConfig, setPrepaymentPenaltyConfig] = useState<PrepaymentPenalty[]>([])
  // What the settings below currently amount to for this loan, computed by the same function that
  // charges the interest, so the date shown is the date being charged from.
  const [defaultStatus, setDefaultStatus] = useState<{
    inDefault: boolean
    since: string
    days: number
    rate: number
  } | null>(null)
  const paragraphObj = paragraphs(inputs)

  const applyPenaltyResponse = (res: any, prepaymentList: PrepaymentPenalty[]) => {
    if (!res?.success) return
    const d = res.data
    setDefaultStatus(res.defaultStatus || null)
    setData(d)
    const nInputs = declarationsPenaltyValidate(d, { prepaymentPenaltyConfig: prepaymentList })
    if (nInputs.retroactiveToDayDefaultRate) {
      nInputs.retroactiveToDayDefaultRate.visible = !!String(d.retroactiveToDayDefaultRate ?? '').trim()
    }
    if (isShared) {
      Object.keys(nInputs).forEach((key) => {
        nInputs[key].disabled = true
        nInputs[key].readOnly = true
      })
    }
    setInputs(nInputs)
  }

  const initLoad = async () => {
    const [rawPrepaymentConfig, res] = await Promise.all([getAdminConfig('prepaymentPenalties'), getPenalty()])
    const prepaymentList = asPrepaymentPenaltyList(rawPrepaymentConfig)
    setPrepaymentPenaltyConfig(prepaymentList)
    applyPenaltyResponse(res, prepaymentList)
    setLoading(false)
  }

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      const prepaymentList: PrepaymentPenalty[] = []
      setPrepaymentPenaltyConfig(prepaymentList)
      applyPenaltyResponse(res, prepaymentList)
      setLoading(false)
      return
    }
    setLoading(true)
    setLoanNumber()
    initLoad()
  }, [isShared, shared?.pageKey, shared?.pageData])

  const onChange = async (key: string, value: any) => {
    if (isShared) return
    let temp = cloneDeep(data)
    temp[key] = value

    const radios: Record<string, string[]> = {
      noPrepaymentPenalty: ['anyPrepaymentPenalty', 'otherPrepaymentPenalty'],
      anyPrepaymentPenalty: ['noPrepaymentPenalty', 'otherPrepaymentPenalty'],
      otherPrepaymentPenalty: ['noPrepaymentPenalty', 'anyPrepaymentPenalty'],
    }
    if (radios[key] && value === true) {
      radios[key].forEach((k) => (temp[k] = false))
    }

    setData(temp)
    const nInputs = declarationsPenaltyValidate(temp, { prepaymentPenaltyConfig })
    setInputs(nInputs)
    setValueChanged(true)
  }

  const onSubmit = async () => {
    if (isShared) return
    const temp = cloneDeep(data)
    Object.keys(temp).forEach((key) => {
      // data can hold keys with no matching input (e.g. retroactiveToDayDefaultRate,
      // radio-group flags, server-only fields) — leave those untouched.
      if (inputs[key]) temp[key] = ConvertInputValue(inputs[key], temp[key])
    })
    setLoading(true)
    await postPenalty(temp)
    setLoading(false)
    setValueChanged(false)
  }

  const percentageOptionElement = useMemo(() => {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
          value={data.percentageOption ?? '-'}
          onChange={(e) => {
            onChange('percentageOption', e.target.value)
          }}
          disabled={isShared || inputs.percentageOption?.disabled}
        >
          <option value="-" disabled>
            {' '}
          </option>
          <option value="Total Pmt">Total Pmt</option>
          <option value="P & I">P & I</option>
        </select>
      </div>
    )
  }, [data.percentageOption, inputs.percentageOption, isShared])

  const defaultAfterUnitElement = useMemo(() => {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
          value={data.defaultAfterUnit ?? '-'}
          onChange={(e) => {
            onChange('defaultAfterUnit', e.target.value)
          }}
          disabled={isShared || inputs.defaultAfterUnit?.disabled}
        >
          <option value="-" disabled>
            {' '}
          </option>
          <option value="Days">Days</option>
          <option value="Weeks">Weeks</option>
          <option value="Months">Months</option>
        </select>
      </div>
    )
  }, [data.defaultAfterUnit, inputs.defaultAfterUnit, isShared])

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl flex items-center gap-1 font-bold">
          <span>Penalties</span>
        </h2>
        {!isShared && (
          <Button color="blue" onClick={onSubmit} loading={loading} disabled={!valueChanged} className="mb-0">
            Save Changes
          </Button>
        )}
      </div>
      <LayoutLoading show={loading} />
      {!isShared && <SaveChanges show={valueChanged} label="Save Changes" onSave={onSubmit} />}
      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          input.value = data[key]
          if (!input.visible) return
          if (key === 'percentage') input.additionalElements = percentageOptionElement
          if (key === 'defaultAfter') input.additionalElements = defaultAfterUnitElement
          return (
            <>
              <div key={index} className={`col-span-${input.span || 1} ${input.className}`}>
                <RenderInput input={input} Key={key} onChange={(key: string, value: any) => onChange(key, value)} />
              </div>
              {key === 'defaultRateValue' && defaultStatus && (
                <div className="col-span-full pb-4">
                  {defaultStatus.inDefault ? (
                    <div className="inline-flex flex-wrap items-center gap-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm dark:border-rose-900 dark:bg-rose-950">
                      <span className="font-semibold text-rose-700 dark:text-rose-300">
                        Default interest since {formatDate(defaultStatus.since)}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {defaultStatus.days > 0
                          ? `${defaultStatus.days} day${defaultStatus.days === 1 ? '' : 's'} at ${
                              defaultStatus.rate
                            }%, charged from that date forward`
                          : `at ${defaultStatus.rate}%, held in force by the Indefinite setting even though the loan is current`}
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Default interest is not running on this loan today.
                    </div>
                  )}
                </div>
              )}
              {key === 'anyPrepaymentPenalty' && (
                <div className="flex flex-wrap items-end col-span-full pl-14 pr-5 pb-8">
                  {renderParagraph(paragraphObj, 0, isShared ? () => {} : onChange)}
                </div>
              )}
            </>
          )
        })}
      </div>
    </div>
  )
}
